import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Gavel, Check, X } from "lucide-react";

export default function IcralikPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/cari/customers").then((r) => setRows((r.data || []).filter((c) => c.icralik && !c.icraya_aktarildi))).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(`${r.musteri} müşterisinin tüm cari kayıtları İcra Kayıtlarına aktarılacak. Onaylıyor musunuz?`)) return;
    setBusy(r.musteri);
    try {
      await api.post("/cari/musteri-to-icra", { musteri: r.musteri });
      toast.success(`${r.musteri} İcra Kayıtlarına aktarıldı`);
      load();
    } catch (e) { toast.error("Aktarım başarısız"); } finally { setBusy(""); }
  };
  const reject = async (r) => {
    setBusy(r.musteri);
    try {
      await api.post("/cari/musteri-icralik", { musteri: r.musteri, icralik: false });
      toast.success("İcralık işareti kaldırıldı");
      load();
    } catch (e) { toast.error("İşlem başarısız"); } finally { setBusy(""); }
  };

  return (
    <Layout>
      <div className="kicker mb-1">CARİ → İCRA</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">İcralık Onay Paneli</h1>
      <p className="text-sm text-[#8C8377] mb-6">Yetkililerin "İcralık" işaretlediği müşteriler burada listelenir. Yönetici onayı ile İcra Kayıtlarına aktarılır.</p>

      <div className="bg-white border border-[#E7DECC] rounded-sm" data-testid="icralik-panel">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
          <Gavel className="w-4 h-4 text-[#B8933F]" />
          <span className="font-heading font-bold text-sm">Onay Bekleyen İcralık Müşteriler</span>
          <span className="ml-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{rows.length}</span>
        </div>
        {loading ? (
          <div className="px-4 py-10 text-center text-[#8C8377] text-sm">Yükleniyor...</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-[#8C8377] text-sm" data-testid="icralik-empty">Onay bekleyen icralık müşteri yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
                  <th className="px-4 py-2.5 kicker text-left">Müşteri</th>
                  <th className="px-4 py-2.5 kicker text-left">Yetkili</th>
                  <th className="px-4 py-2.5 kicker text-right">Vade Sayısı</th>
                  <th className="px-4 py-2.5 kicker text-right">Toplam Kalan</th>
                  <th className="px-4 py-2.5 kicker text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key || r.musteri} className="border-b border-[#E7DECC] hover:bg-[#EFE7D6] transition-colors duration-150" data-testid={`icralik-row-${r.musteri}`}>
                    <td className="px-4 py-2 font-medium cursor-pointer" onClick={() => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)}>{r.musteri}</td>
                    <td className="px-4 py-2 text-[#8C8377]">{r.yetkili || "-"}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.vade_sayisi}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#A63A3A]">{fmt(r.toplam_kalan)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isAdmin ? (
                        <div className="flex justify-end gap-2">
                          <button disabled={busy === r.musteri} onClick={() => approve(r)} data-testid={`icralik-approve-${r.musteri}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2F6E4E] hover:bg-[#2AA249] text-white rounded-sm text-xs font-semibold transition-colors duration-150 disabled:opacity-60"><Check className="w-3.5 h-3.5" /> İcraya Aktar</button>
                          <button disabled={busy === r.musteri} onClick={() => reject(r)} data-testid={`icralik-reject-${r.musteri}`} className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#E7DECC] text-[#8C8377] hover:text-[#A63A3A] rounded-sm text-xs font-semibold transition-colors duration-150 disabled:opacity-60"><X className="w-3.5 h-3.5" /> İptal</button>
                        </div>
                      ) : <span className="text-xs text-[#B8933F] font-semibold">Onay bekliyor</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
